/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { FormType, LocalisationService } from '@myprofile-ui/myprofile-localisation-api'
import { PidxI18nService } from '@myprofile-ui/pidx-utils'
import { Store } from '@ngxs/store'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

export const FALLBACK_COUNTRY = 'am'

// Country codes that should not appear as selectable options in the country dropdown.
// These are sub-regions of the United Arab Emirates (AE) and not standalone countries.
const EXCLUDED_COUNTRY_CODES = ['XA', 'XD']

@Injectable({
  providedIn: 'root',
})
export class MyProfileAddressDataService {
  private store = inject(Store)
  private i18nService = inject(PidxI18nService)
  private localisationService = inject(LocalisationService)

  public getTranslations(): Observable<object> {
    const { country, locale } = this.store.selectSnapshot(({ addressWidget }) => addressWidget)
    return this.localisationService.getTranslationFor('shared-webcomponents', country, locale)
  }

  public getLayout(countryCode: string, type: FormType): Observable<object> {
    if (!this.i18nService.isCountrySupported(countryCode)) {
      countryCode = FALLBACK_COUNTRY
    }

    return this.localisationService.getLayoutFor(type, countryCode)
  }

  public getSchema(countryCode: string, type: FormType, locale: string): Observable<object> {
    if (!this.i18nService.isCountrySupported(countryCode)) {
      countryCode = FALLBACK_COUNTRY
    }

    return this.localisationService
      .getSchemaFor(type, countryCode, locale)
      .pipe(map((schema: any[]) => this.removeExcludedCountryOptions(schema)))
  }

  private removeExcludedCountryOptions(schema: any[]): any[] {
    return schema.map((field) => {
      if (field.type === 'SELECT' && field.id === 'country' && Array.isArray(field.options)) {
        return {
          ...field,
          options: field.options.filter((option: { value: string }) => !EXCLUDED_COUNTRY_CODES.includes(option.value)),
        }
      }
      return field
    })
  }
}
